import { useContext, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenuItem,
  Button
} from '@@/ui/primitives';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { DownloadIcon, RefreshCwIcon } from 'lucide-react';
import { Spinner } from '@@/ui/components/Spinner';
import { AuthContext } from '@@/contexts/AuthContext';
import { DfcApiClient } from '../../../../../api/dfc-api-client';
import { ResultValue } from '@@/types/ApiResult';

enum ExportStatus {
  IDLE = 'IDLE',
  EXPORTING = 'EXPORTING',
  DOWNLOADING = 'DOWNLOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

export function EntityExportDialog(props: { baseEntityId: string }) {
  const [status, setStatus] = useState<ExportStatus>(ExportStatus.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<{
    itemCount: number;
    entityCount: number;
  } | null>(null);

  const { instanceData } = useContext(AuthContext);

  const handleExport = async () => {
    if (!instanceData?.instanceId) {
      setError('Missing required context data');
      setStatus(ExportStatus.ERROR);
      return;
    }

    setStatus(ExportStatus.EXPORTING);
    setError(null);
    setExportInfo(null);

    try {
      const dfcApiClient = new DfcApiClient();
      const result = await dfcApiClient.tools.bulkExport({
        input: { baseEntityId: props.baseEntityId },
        queryString: {
          instanceId: instanceData.instanceId,
          region: instanceData.region
        }
      });

      if (!ResultValue.ok(result)) {
        throw new Error(result.message ?? 'Failed to export data');
      }

      setExportInfo({
        itemCount: result.data.itemCount,
        entityCount: result.data.entityCount
      });

      setStatus(ExportStatus.DOWNLOADING);

      const response = await fetch(result.data.url);
      if (!response.ok) {
        throw new Error('Failed to download export file');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `dfc-export-${props.baseEntityId}-${new Date().toISOString()}.jsonl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      setStatus(ExportStatus.SUCCESS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setStatus(ExportStatus.ERROR);
    }
  };

  const isLoading = status === ExportStatus.EXPORTING || status === ExportStatus.DOWNLOADING;

  return (
    <Dialog>
      <DialogTrigger>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <DownloadIcon />
          Export
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className='font-sans font-[450]'>
        <DialogHeader>
          <DialogTitle>Export</DialogTitle>
          <DialogDescription>
            Get a{' '}
            <a href='https://jsonlines.org/' target='_blank' className='text-blue'>
              JSON Lines
            </a>{' '}
            file containing this Entity, it's children and all associated items to your computer.
          </DialogDescription>
        </DialogHeader>
        {status === ExportStatus.IDLE && (
          <p className='text-sm text-muted-foreground'>
            Click the button below to start the export process. This may take several seconds depending on the amount of
            data.
          </p>
        )}

        {status === ExportStatus.EXPORTING && (
          <div className='flex items-center gap-3'>
            <Spinner size='sm' />
            <span className='text-sm'>Preparing export...</span>
          </div>
        )}

        {status === ExportStatus.DOWNLOADING && (
          <div className='flex items-center gap-3'>
            <Spinner size='sm' />
            <span className='text-sm'>Downloading file...</span>
          </div>
        )}

        {status === ExportStatus.SUCCESS && exportInfo && (
          <div className='text-sm grid grid-cols-2 gap-4'>
            <div className='flex flex-row justify-between bg-muted-foreground/10 p-3 rounded gap-1'>
              <span className='font-medium'>Entities</span>
              {exportInfo.entityCount.toLocaleString()}
            </div>
            <div className='flex flex-row justify-between bg-muted-foreground/10 p-3 rounded gap-1'>
              <span className='font-medium'>Items</span>
              {exportInfo.itemCount.toLocaleString()}
            </div>

            <div className='col-span-2 flex'>
              <span className='text-sm text-muted-foreground'>
                Your Export has been processed. A file has been downloaded containing the exported data.
              </span>
            </div>
            <div className='col-span-2 flex justify-center'>
              <Button
                variant='outline'
                size='default'
                onClick={() => {
                  setStatus(ExportStatus.IDLE);
                  setExportInfo(null);
                  setError(null);
                }}
              >
                <RefreshCwIcon />
                Restart
              </Button>
            </div>
          </div>
        )}

        {status === ExportStatus.ERROR && error && <div className='text-sm text-destructive'>{error}</div>}
        <DialogFooter>
          {status !== ExportStatus.SUCCESS && (
            <Button onClick={handleExport} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner size='sm' className='mr-2' />
                  {status === ExportStatus.EXPORTING ? 'Exporting...' : 'Downloading...'}
                </>
              ) : (
                'Start export'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
